import React, { useCallback, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  IconButton,
  Paper
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'

const useStyles = makeStyles((theme) => ({
  container: {
  },
  tableContainer: {
    maxHeight: 600,
    overflow: 'auto',
    marginBottom: theme.spacing(2)
  },
  table: {
    minWidth: 900
  },
  cell: {
    padding: theme.spacing(1),
    minWidth: 120
  },
  smallCell: {
    padding: theme.spacing(0.5),
    minWidth: 60,
    maxWidth: 80
  },
  textField: {
    '& .MuiInputBase-input': {
      padding: theme.spacing(0.5, 1),
      fontSize: '0.875rem'
    }
  },
  checkbox: {
    padding: theme.spacing(0.5)
  },
  addButton: {
    margin: theme.spacing(1, 0)
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  summary: {
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.info.contrastText
  }
}))

const ApiInputsEditTable = forwardRef(({ initialInputs = [] }, ref) => {
  const classes = useStyles()
  const [inputs, setInputs] = useState(() =>
    initialInputs.map((input, index) => ({
      ...input,
      tempId: input.catalogApiInputId || `temp_${index}`,
      isNew: !input.catalogApiInputId
    }))
  )

  useImperativeHandle(ref, () => ({
    getData: () => inputs.map(input => {
      const { tempId, isNew, modified, ...data } = input
      return data
    }),
    getChanges: () => ({
      added: inputs.filter(input => input.isNew).length,
      modified: inputs.filter(input => !input.isNew && input.modified).length
    })
  }))

  const updateInput = useCallback((tempId, field, value) => {
    setInputs(prev => prev.map(input =>
      input.tempId === tempId
        ? { ...input, [field]: value, modified: !input.isNew || input.modified }
        : input
    ))
  }, [])

  const deleteInput = useCallback((tempId) => {
    setInputs(prev => prev.filter(input => input.tempId !== tempId))
  }, [])

  const addInput = useCallback(() => {
    const newInput = {
      tempId: `temp_${Date.now()}`,
      isNew: true,
      modified: false,
      catalogApiInputId: null,
      catalogDatasourceId: inputs[0]?.catalogDatasourceId || null,
      inputName: '',
      displayName: '',
      dataType: 'string',
      isRequired: false,
      defaultValue: null,
      validationRule: null,
      description: null,
      ordinal: Math.max(...inputs.map(i => i.ordinal || 0), 0) + 1
    }
    setInputs(prev => [...prev, newInput])
  }, [inputs])

  const handleTextFieldChange = useCallback((tempId, field) => (event) => {
    const value = event.target.value
    updateInput(tempId, field, value === '' ? null : value)
  }, [updateInput])

  const handleCheckboxChange = useCallback((tempId, field) => (event) => {
    updateInput(tempId, field, event.target.checked)
  }, [updateInput])

  const handleNumberChange = useCallback((tempId, field) => (event) => {
    const value = event.target.value
    const numValue = value === '' ? null : parseInt(value, 10)
    updateInput(tempId, field, isNaN(numValue) ? null : numValue)
  }, [updateInput])

  return (
    <div>
      <div className={classes.addButton}>
        <IconButton
          color='primary'
          onClick={addInput}
          size='small'
        >
          <AddIcon />
        </IconButton>
        <Typography variant='body2' component='span' style={{ marginLeft: 8 }}>
          Add Input
        </Typography>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table} size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>Actions</TableCell>
              <TableCell className={classes.headerCell}>Input Name</TableCell>
              <TableCell className={classes.headerCell}>Display Name</TableCell>
              <TableCell className={classes.headerCell}>Data Type</TableCell>
              <TableCell className={classes.headerCell}>Required</TableCell>
              <TableCell className={classes.headerCell}>Ordinal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inputs.map((input) => (
              <TableRow key={input.tempId}>
                <TableCell className={classes.smallCell}>
                  <IconButton
                    size='small'
                    onClick={() => deleteInput(input.tempId)}
                    color='secondary'
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </TableCell>

                <TableCell className={classes.cell}>
                  <TextField
                    value={input.inputName || ''}
                    onChange={handleTextFieldChange(input.tempId, 'inputName')}
                    variant='outlined'
                    size='small'
                    fullWidth
                    className={classes.textField}
                    placeholder='param_name'
                  />
                </TableCell>

                <TableCell className={classes.cell}>
                  <TextField
                    value={input.displayName || ''}
                    onChange={handleTextFieldChange(input.tempId, 'displayName')}
                    variant='outlined'
                    size='small'
                    fullWidth
                    className={classes.textField}
                    placeholder='Display Name'
                  />
                </TableCell>

                <TableCell className={classes.cell}>
                  <TextField
                    value={input.dataType || ''}
                    onChange={handleTextFieldChange(input.tempId, 'dataType')}
                    variant='outlined'
                    size='small'
                    fullWidth
                    className={classes.textField}
                    placeholder='string'
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <Checkbox
                    checked={!!input.isRequired}
                    onChange={handleCheckboxChange(input.tempId, 'isRequired')}
                    className={classes.checkbox}
                    color='primary'
                  />
                </TableCell>

                <TableCell className={classes.smallCell}>
                  <TextField
                    value={input.ordinal || ''}
                    onChange={handleNumberChange(input.tempId, 'ordinal')}
                    variant='outlined'
                    size='small'
                    type='number'
                    className={classes.textField}
                    required
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
})

ApiInputsEditTable.propTypes = {
  initialInputs: PropTypes.array
}

ApiInputsEditTable.displayName = 'ApiInputsEditTable'

const useSubmitter = (dataSource, inputsRef, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: modifyApiInputs } = dataCatalog.useModifyDatasourceApiInputs()

  const onSubmit = useCallback(async () => {
    if (!inputsRef.current) {
      setError('Unable to access inputs table')
      return
    }

    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      apiInputs: inputsRef.current.getData()
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await modifyApiInputs(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the API inputs')
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, inputsRef, onComplete, modifyApiInputs])

  return {
    onSubmit,
    processing,
    error
  }
}

function EditApiInputsForm ({ dataSource, onCancel, onComplete }) {
  const classes = useStyles()
  const inputsRef = useRef()

  const { onSubmit, processing, error } = useSubmitter(dataSource, inputsRef, onComplete)

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No data source provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ApiInputsEditTable
            ref={inputsRef}
            initialInputs={dataSource.apiInputs || []}
          />
        </Grid>

        <Grid item xs={12}>
          <SydModalActions>
            {error && <div className='__error'>{error}</div>}

            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={onSubmit}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

EditApiInputsForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    apiInputs: PropTypes.array
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default EditApiInputsForm
